/**
 * The standard content-pane wrapper for a top-level page. Centralizes the
 * `mx-auto px-4 md:px-8 py-8` spacing every section shares so the padding/
 * margins stay consistent across the app (the Tracker drifted from this once —
 * heading pinned to the top with no `py` — which is why this exists).
 *
 * `width` picks the max-width band; `space` adds the common vertical rhythm
 * between direct children (most pages want `space-y-6`). Pages with a special
 * layout (the Drills side-rail flex, the Recent scroll container) can still
 * pass extra classes via `className`.
 */

import type { ReactNode } from "react";

const WIDTHS = {
  narrow: "max-w-2xl",
  default: "max-w-3xl",
  wide: "max-w-4xl",
  full: "max-w-6xl",
} as const;

export interface PageContainerProps {
  width?: keyof typeof WIDTHS;
  /** Apply `space-y-6` between direct children (the app's default rhythm). */
  space?: boolean;
  className?: string;
  children: ReactNode;
}

export function PageContainer({
  width = "default",
  space = true,
  className,
  children,
}: PageContainerProps) {
  return (
    <div
      className={[
        WIDTHS[width],
        "mx-auto px-4 md:px-8 py-8",
        space ? "space-y-6" : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}
